import { useState } from "react";
import { Button, Form, Select, DatePicker, message, notification } from "antd";
import { HomeOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import PageBreadcrumb from "../../components/breadcrumb";
import AddMerchantModal from "../../components/modals/AddMerchantModal";
import VendorTransactionFields from "../../components/modals/AddVendorTransactionModal/VendorTransactionFields";
import { useAddVendorTransactionMutation } from "../../services/vendor";
import { useGetMerchantsQuery } from "../../services/merchant";
import { useGetBranchesQuery } from "../../services/branches";
import { walletApi } from "../../services/wallet";
import { AllVendorsRoute, ExpenseHistoryRoute, Home } from "../../routes/routepath";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface AddExpenseFormValues {
  merchant:        string;
  branches:        string[];
  transactionDate: dayjs.Dayjs;
  amount:       number;
  paymentType:  'full' | 'partial';
  paidVia:      'wallet' | 'outstanding';
  paymentMode:  'cash' | 'online';
  paidAmount?:  number;
  bill?:        string;
  paymentBill?: string;
  description?: string;
}

const breadcrumbItems = [
  { label: <HomeOutlined />, to: Home },
  { label: "All Vendors", to: AllVendorsRoute },
  { label: "Add Expense" },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const AddExpense = () => {
  const [form]   = Form.useForm<AddExpenseFormValues>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [addVendorModalOpen, setAddVendorModalOpen] = useState(false);

  const { data: merchantsResp, isFetching: merchantsLoading } = useGetMerchantsQuery({ status: 'active', limit: 1000 });
  const { data: branchesResp, isFetching: branchesLoading } = useGetBranchesQuery();
  const [addVendorTransaction, { isLoading: submitting }] = useAddVendorTransactionMutation();

  const merchantOptions = ((merchantsResp as any)?.data || [])
    .filter((m: any) => m.isActive !== false)
    .map((m: any) => ({
      value: m._id,
      label: m.name,
    }));

  const branchList = Array.isArray(branchesResp)
    ? branchesResp
    : (Array.isArray((branchesResp as any)?.data) ? (branchesResp as any).data : []);
  const branchOptions = branchList.map((b: any) => ({
    value: b._id || b.id,
    label: b.name,
  }));

  const paymentType = Form.useWatch('paymentType', form);
  const isPartial    = paymentType === 'partial';

  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
    if (!errorFields?.length) return;
    const labels = errorFields.map(f => {
      const name = Array.isArray(f.name) ? f.name[0] : f.name;
      return String(name).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    });
    notification.error({
      message:     'Required Fields Missing',
      description: [...new Set(labels)].join(', '),
      placement:   'topRight',
      duration:    4,
    });
  };

  const onFinish = async (values: AddExpenseFormValues) => {
    const paidAmount = isPartial ? (values.paidAmount ?? 0) : values.amount;
    const dueAmount  = isPartial ? Math.max(values.amount - paidAmount, 0) : 0;
    const branchFields = values.branches.length === 1
      ? { branchId:  values.branches[0] }
      : { branchIds: values.branches };
    try {
      await addVendorTransaction({
        merchantId:      values.merchant,
        transactionDate: dayjs(values.transactionDate).format('YYYY-MM-DD HH:mm:ss'),
        amount:      values.amount,
        paymentType: values.paymentType,
        paidVia:     values.paidVia,
        paymentMode: values.paymentMode,
        bill:        values.bill,
        paymentBill: values.paymentMode === 'online' ? values.paymentBill : undefined,
        description: values.description,
        paidAmount,
        dueAmount,
        ...branchFields,
      }).unwrap();
      message.success('Expense added successfully');
      form.resetFields();
      dispatch(walletApi.util.invalidateTags(['Wallet']));
      navigate(ExpenseHistoryRoute);
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  return (
    <div className="add-expense-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Add Expense</h2>
        <PageBreadcrumb items={breadcrumbItems} />
      </div>

      <Form form={form} layout="vertical" onFinish={onFinish} onFinishFailed={onFinishFailed} className="add-expense-form">
        <div className="vendor-row">
          <Form.Item name="merchant" label="Merchant" rules={[{ required: true, message: 'Please select a merchant' }]}>
            <Select
              placeholder="Search merchant..."
              loading={merchantsLoading}
              showSearch
              optionFilterProp="label"
              options={merchantOptions}
            />
          </Form.Item>
          <div className="add-vendor-btn-wrap">
            <label>&nbsp;</label>
            <Button
              className="add-vendor-btn"
              icon={<PlusOutlined />}
              onClick={() => setAddVendorModalOpen(true)}
            >
              Add Merchant
            </Button>
          </div>
        </div>

        <Form.Item name="branches" label="Branch" rules={[{ required: true, message: 'Please select at least one branch' }]}>
          <Select
            mode="multiple"
            placeholder="Select branch(es)"
            loading={branchesLoading}
            optionFilterProp="label"
            options={branchOptions}
          />
        </Form.Item>

        <Form.Item
          name="transactionDate"
          label="Date & Time"
          initialValue={dayjs()}
          rules={[{ required: true, message: 'Please select date & time' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            showTime={{ format: 'hh:mm A', use12Hours: true }}
            format="DD-MM-YYYY hh:mm A"
            placeholder="Select date & time"
          />
        </Form.Item>

        <VendorTransactionFields form={form} />

        <div className="footer-buttons">
          <Button type="default" onClick={() => navigate(AllVendorsRoute)}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" className="save-btn" loading={submitting}>
            {submitting ? 'Adding...' : 'Add Expense'}
          </Button>
        </div>
      </Form>

      <AddMerchantModal
        visible={addVendorModalOpen}
        onCancel={() => setAddVendorModalOpen(false)}
        onSuccess={(merchant) => {
          if (merchant?._id) form.setFieldsValue({ merchant: merchant._id });
        }}
      />
    </div>
  );
};

export default AddExpense;
